import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { authService, type User } from '../services/auth';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  oauthError: string | null;
  login: (email: string, password: string) => Promise<{ user: User }>;
  loginWithGoogle: () => Promise<void>;
  register: (email: string, password: string, fullName: string) => Promise<void>;
  logout: () => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  clearOauthError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [oauthError, setOauthError] = useState<string | null>(null);
  // undefined = aún no inicializado | null = sin sesión | Session = sesión activa
  const [authSession, setAuthSession] = useState<Session | null | undefined>(undefined);
  const lastUserId = useRef<string | null>(null);

  // Efecto 1: solo escucha eventos de auth (sin async) y obtiene la sesión inicial
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setAuthSession(session ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuthSession(session ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Efecto 2: reacciona a cambios de sesión y carga el perfil del usuario
  useEffect(() => {
    if (authSession === undefined) return; // Aún inicializando

    if (!authSession) {
      setUser(null);
      lastUserId.current = null;
      setLoading(false);
      return;
    }

    // Evitar recargar perfil si el usuario no cambió (e.g. token refresh silencioso)
    if (lastUserId.current === authSession.user.id && user !== null) {
      setLoading(false);
      return;
    }

    lastUserId.current = authSession.user.id;

    authService
      .getUserFromSession(authSession.user.id, authSession.user.email || '')
      .then((u) => {
        if (!u) {
          setOauthError('Tu cuenta no está registrada en el sistema. Contacta al administrador.');
          setUser(null);
          supabase.auth.signOut();
          return;
        }
        if (u.status === 'pending') {
          setOauthError('Tu cuenta está pendiente de activación. Contacta al administrador.');
          setUser(null);
          lastUserId.current = null;
          supabase.auth.signOut();
          return;
        }
        if (u.status === 'blocked') {
          setOauthError('Tu cuenta ha sido bloqueada. Contacta al administrador.');
          setUser(null);
          lastUserId.current = null;
          supabase.auth.signOut();
          return;
        }
        setUser(u);
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, [authSession]); // eslint-disable-line react-hooks/exhaustive-deps

  async function login(email: string, password: string) {
    const response = await authService.login(email, password);
    setUser(response.user);
    return { user: response.user };
  }

  async function loginWithGoogle() {
    setOauthError(null);
    await authService.loginWithGoogle();
  }

  async function register(email: string, password: string, fullName: string) {
    await authService.register(email, password, fullName);
  }

  async function logout() {
    await authService.logout();
    setUser(null);
  }

  async function updatePassword(newPassword: string) {
    await authService.updateUserPassword(newPassword);
  }

  function clearOauthError() {
    setOauthError(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, oauthError, login, loginWithGoogle, register, logout, updatePassword, clearOauthError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
